import {
  type CSSProperties,
  type FocusEvent,
  type KeyboardEvent,
  type MouseEvent,
  type PointerEvent,
  type ReactElement,
  type ReactNode,
  type RefObject,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { type VariantProps, cva } from 'class-variance-authority';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { TOAST_HOST_ATTRIBUTE, subscribeModalSlots, topModalSlot } from '../lib/modal-layers.js';
import { cn } from '../lib/utils.js';

/**
 * Transient confirmations and failures.
 *
 * Callers pass strings that are ALREADY translated (`toast({ title: t('order.saved') })`)
 * rather than keys, so a message can carry interpolated values — an amount, a customer's
 * name — without this component knowing anything about the domain.
 *
 * On a phone the viewport sits at the top: the bottom of the screen belongs to the navigation
 * bar and to the thumb.
 *
 * Each toast is its own live region, so a screen reader announces it: politely for news, and
 * assertively for a failure. A toast closes itself after its duration, but never while the
 * pointer rests on it or focus is inside it — the clock resumes with whatever time was left.
 * Swiping it right dismisses it, and F8 moves focus to the viewport, from where Tab reaches
 * each toast's buttons.
 *
 * A modal `<dialog>` paints in the browser's top layer and makes everything outside it inert, so a
 * viewport left on the page would be hidden behind it, unclickable and unheard. While a modal is
 * open the viewport therefore moves into it (see `lib/modal-layers.ts`) and comes back out when it
 * closes. The toasts themselves are never re-created by the move.
 */

const toastVariants = cva(
  'pointer-events-auto flex w-full items-start gap-3 rounded-lg border p-4 shadow-lg data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-80 data-[state=open]:slide-in-from-top-2 sm:data-[state=open]:slide-in-from-bottom-2',
  {
    variants: {
      variant: {
        default: 'border-border bg-card text-card-foreground',
        success: 'border-success/30 bg-success/10 text-foreground',
        warning: 'border-warning/30 bg-warning/10 text-foreground',
        destructive: 'border-destructive/30 bg-destructive/10 text-foreground',
      },
    },
    defaultVariants: { variant: 'default' },
  },
);

export type ToastVariant = NonNullable<VariantProps<typeof toastVariants>['variant']>;

export interface ToastOptions {
  /** Already translated. */
  title: string;
  description?: string;
  variant?: ToastVariant;
  durationMs?: number;
  action?: { label: string; onClick: () => void };
}

interface ToastRecord extends ToastOptions {
  id: number;
  /** False once dismissed: the toast stays mounted while its exit animation plays. */
  open: boolean;
}

export interface ToastApi {
  toast: (options: ToastOptions) => void;
  dismiss: (id: number) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

export function useToast(): ToastApi {
  const api = useContext(ToastContext);
  if (!api) {
    throw new Error('useToast must be used inside <ToastProvider>');
  }
  return api;
}

const DEFAULT_DURATION_MS = 5_000;
/** A failure stays on screen long enough to be read and acted on. */
const FAILURE_DURATION_MS = 8_000;

/** How far right a toast must be dragged before letting go dismisses it. */
const TOAST_SWIPE_THRESHOLD_PX = 50;
/** Movement below this is a tap, not the start of a swipe. A finger wobbles more than a mouse. */
const TOUCH_MOVE_BUFFER_PX = 10;
const POINTER_MOVE_BUFFER_PX = 2;
/** The exit animation's own `animationend` normally unmounts a toast; this is the backstop. */
const EXIT_FALLBACK_MS = 1_000;

const VIEWPORT_LABEL = 'Notifications (F8)';
const VIEWPORT_HOTKEY = 'F8';

const useIsomorphicLayoutEffect = typeof window === 'undefined' ? useEffect : useLayoutEffect;

/**
 * The element the viewport is rendered into: on the page, or — while a modal dialog is open —
 * inside that dialog, as a popover in the top layer so it paints above the dialog and ignores the
 * dialog's own transform and overflow. Created and moved by hand, because React must never be
 * the one to remove it from a parent it has since left.
 */
function useToastHost(): HTMLElement | null {
  const [host, setHost] = useState<HTMLElement | null>(null);

  useIsomorphicLayoutEffect(() => {
    const element = document.createElement('div');
    element.setAttribute(TOAST_HOST_ATTRIBUTE, '');

    const leaveTopLayer = () => {
      if (!element.hasAttribute('popover')) return;
      try {
        element.hidePopover();
      } catch {
        // Not showing, or not connected: already out.
      }
      element.removeAttribute('popover');
    };

    const place = () => {
      const slot = topModalSlot();
      const parent = slot ?? document.body;
      if (element.parentNode !== parent) {
        leaveTopLayer();
        parent.appendChild(element);
      }
      if (slot === null || typeof element.showPopover !== 'function') {
        // On the page: no box of its own, so the viewport inside positions itself against the
        // window as it always did. In a dialog without popover support it is still reachable and
        // heard — just not lifted out of the dialog's own box.
        leaveTopLayer();
        element.style.cssText = 'display: contents';
        return;
      }
      // In a dialog: a zero-sized popover, lifted into the top layer above it. The browser's own
      // popover styles would otherwise give it a border, a backdrop colour and a centred box.
      element.style.cssText =
        'margin: 0; padding: 0; border: 0; width: 0; height: 0; overflow: visible; background: transparent';
      element.setAttribute('popover', 'manual');
      try {
        if (!element.matches(':popover-open')) element.showPopover();
      } catch {
        // Not connected yet; the next move shows it.
      }
    };

    place();
    setHost(element);
    const stopWatching = subscribeModalSlots(place);

    return () => {
      stopWatching();
      leaveTopLayer();
      element.remove();
    };
  }, []);

  return host;
}

export function ToastProvider({ children }: { children: ReactNode }): ReactElement {
  const [toasts, setToasts] = useState<ToastRecord[]>([]);
  const nextId = useRef(0);
  const viewportRef = useRef<HTMLDivElement>(null);
  const host = useToastHost();

  const dismiss = useCallback((id: number) => {
    setToasts((current) =>
      current.some((item) => item.id === id && item.open)
        ? current.map((item) => (item.id === id ? { ...item, open: false } : item))
        : current,
    );
  }, []);

  const remove = useCallback((id: number) => {
    setToasts((current) => current.filter((item) => item.id !== id));
  }, []);

  const toast = useCallback((options: ToastOptions) => {
    nextId.current += 1;
    const id = nextId.current;
    setToasts((current) => [...current, { ...options, id, open: true }]);
  }, []);

  const api = useMemo<ToastApi>(() => ({ toast, dismiss }), [dismiss, toast]);

  useEffect(() => {
    const onKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.code === VIEWPORT_HOTKEY || event.key === VIEWPORT_HOTKEY) {
        viewportRef.current?.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, []);

  return (
    <ToastContext.Provider value={api}>
      {children}
      {host
        ? createPortal(
            <div
              ref={viewportRef}
              role="region"
              aria-label={VIEWPORT_LABEL}
              tabIndex={-1}
              className="pointer-events-none fixed inset-x-0 top-0 z-[100] flex max-h-screen w-full flex-col gap-2 p-4 sm:inset-x-auto sm:bottom-0 sm:right-0 sm:top-auto sm:max-w-sm"
            >
              {toasts.map((item) => (
                <Toast
                  key={item.id}
                  item={item}
                  viewportRef={viewportRef}
                  onClose={dismiss}
                  onExited={remove}
                />
              ))}
            </div>,
            host,
          )
        : null}
    </ToastContext.Provider>
  );
}

type SwipeState =
  { phase: 'move'; x: number } | { phase: 'cancel' } | { phase: 'end'; x: number } | null;

function Toast({
  item,
  viewportRef,
  onClose,
  onExited,
}: {
  item: ToastRecord;
  viewportRef: RefObject<HTMLDivElement | null>;
  onClose: (id: number) => void;
  onExited: (id: number) => void;
}): ReactElement {
  const { t } = useTranslation();
  const { id, open, variant } = item;
  const ref = useRef<HTMLDivElement>(null);

  const close = useCallback(() => onClose(id), [id, onClose]);

  // ---- auto-dismiss, paused while hovered or focused --------------------------------------
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const [swipe, setSwipe] = useState<SwipeState>(null);
  const remainingMs = useRef(
    item.durationMs ?? (variant === 'destructive' ? FAILURE_DURATION_MS : DEFAULT_DURATION_MS),
  );
  const paused = hovered || focused || swipe?.phase === 'move';

  useEffect(() => {
    if (!open || paused || !Number.isFinite(remainingMs.current)) return;
    const startedAt = Date.now();
    const timer = window.setTimeout(close, Math.max(0, remainingMs.current));
    return () => {
      window.clearTimeout(timer);
      remainingMs.current -= Date.now() - startedAt;
    };
  }, [close, open, paused]);

  // ---- exit: stay mounted until the closing animation ends ---------------------------------
  useLayoutEffect(() => {
    if (open) return;
    const node = ref.current;
    if (!node) {
      onExited(id);
      return;
    }
    // Focus would otherwise fall back to <body>; Radix parked it on the viewport, so do we.
    if (node.contains(document.activeElement)) viewportRef.current?.focus();

    const animationName = window.getComputedStyle(node).animationName;
    if (!animationName || animationName === 'none') {
      onExited(id);
      return;
    }
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      onExited(id);
    };
    const onAnimationEnd = (event: AnimationEvent) => {
      if (event.target === node) finish();
    };
    node.addEventListener('animationend', onAnimationEnd);
    node.addEventListener('animationcancel', onAnimationEnd);
    const fallback = window.setTimeout(finish, EXIT_FALLBACK_MS);
    return () => {
      node.removeEventListener('animationend', onAnimationEnd);
      node.removeEventListener('animationcancel', onAnimationEnd);
      window.clearTimeout(fallback);
    };
  }, [id, onExited, open, viewportRef]);

  // ---- swipe right to dismiss -------------------------------------------------------------
  const pointerStart = useRef<{ x: number; y: number } | null>(null);
  const swiping = useRef(false);
  /** A swipe ends with a click on whatever is under the finger; that click must do nothing. */
  const swallowClick = useRef(false);

  const onPointerDown = (event: PointerEvent<HTMLDivElement>) => {
    // A toast belongs to no dialog or menu, yet pressing it must not count as a press outside
    // the one that is open (a failed save inside a dialog would lose the form). Radix made the
    // viewport a "branch" of every layer; keeping the press from reaching the document does the
    // same for any outside-press listener registered there.
    event.stopPropagation();
    if (!open || event.button !== 0) return;
    pointerStart.current = { x: event.clientX, y: event.clientY };
    swiping.current = false;
    swallowClick.current = false;
  };

  const onPointerMove = (event: PointerEvent<HTMLDivElement>) => {
    const start = pointerStart.current;
    if (!start) return;
    const x = event.clientX - start.x;
    const y = event.clientY - start.y;
    if (!swiping.current) {
      const buffer = event.pointerType === 'touch' ? TOUCH_MOVE_BUFFER_PX : POINTER_MOVE_BUFFER_PX;
      if (x > buffer && x > Math.abs(y)) {
        swiping.current = true;
        const node = event.currentTarget;
        if (typeof node.setPointerCapture === 'function') {
          try {
            node.setPointerCapture(event.pointerId);
          } catch {
            // The pointer may already be gone; the swipe still works without capture.
          }
        }
      } else if (Math.abs(x) > buffer || Math.abs(y) > buffer) {
        // Moving left, up or down: not a dismissal. Let the page scroll.
        pointerStart.current = null;
        return;
      } else {
        return;
      }
    }
    setSwipe({ phase: 'move', x: Math.max(0, x) });
  };

  const endSwipe = (event: PointerEvent<HTMLDivElement>, cancelled: boolean) => {
    const start = pointerStart.current;
    pointerStart.current = null;
    if (!start || !swiping.current) return;
    swiping.current = false;
    swallowClick.current = true;
    const node = event.currentTarget;
    if (typeof node.hasPointerCapture === 'function' && node.hasPointerCapture(event.pointerId)) {
      node.releasePointerCapture(event.pointerId);
    }
    const x = Math.max(0, event.clientX - start.x);
    if (!cancelled && x >= TOAST_SWIPE_THRESHOLD_PX) {
      setSwipe({ phase: 'end', x });
      close();
    } else {
      setSwipe({ phase: 'cancel' });
    }
  };

  const onClickCapture = (event: MouseEvent<HTMLDivElement>) => {
    if (!swallowClick.current) return;
    swallowClick.current = false;
    event.preventDefault();
    event.stopPropagation();
  };

  const swipeStyle: CSSProperties | undefined =
    swipe === null
      ? undefined
      : swipe.phase === 'move'
        ? { transform: `translateX(${swipe.x}px)`, transition: 'none' }
        : swipe.phase === 'cancel'
          ? { transform: 'translateX(0)', transition: 'transform 200ms ease-out' }
          : // Carry on off the right edge from where the finger let go, fading as it goes.
            ({
              transform: `translateX(${swipe.x}px)`,
              '--tw-exit-translate-x': '100%',
            } as CSSProperties);

  // ---- focus and keyboard -----------------------------------------------------------------
  const onFocus = () => setFocused(true);
  const onBlur = (event: FocusEvent<HTMLDivElement>) => {
    const next = event.relatedTarget;
    if (next instanceof Node && event.currentTarget.contains(next)) return;
    setFocused(false);
  };
  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape' && open) {
      // Inside a modal dialog this Escape is the toast's, not the dialog's: stopping it here keeps
      // the dialog's own handler from seeing it, and cancelling it keeps the browser from turning
      // it into a close request for the dialog anyway.
      event.preventDefault();
      event.stopPropagation();
      close();
    }
  };

  const assertive = variant === 'destructive';

  return (
    <div
      ref={ref}
      role={assertive ? 'alert' : 'status'}
      aria-live={assertive ? 'assertive' : 'polite'}
      aria-atomic="true"
      data-state={open ? 'open' : 'closed'}
      data-swipe={swipe?.phase}
      data-swipe-direction="right"
      className={cn(toastVariants({ variant }))}
      style={{ touchAction: 'pan-y', ...swipeStyle }}
      onPointerEnter={() => setHovered(true)}
      onPointerLeave={() => setHovered(false)}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={(event) => endSwipe(event, false)}
      onPointerCancel={(event) => endSwipe(event, true)}
      onClickCapture={onClickCapture}
      onFocus={onFocus}
      onBlur={onBlur}
      onKeyDown={onKeyDown}
    >
      <div className="flex flex-1 flex-col gap-1">
        <div className="text-sm font-semibold">{item.title}</div>
        {item.description ? (
          <div className="text-sm text-muted-foreground">{item.description}</div>
        ) : null}
      </div>
      {item.action ? (
        <button
          type="button"
          onClick={() => {
            item.action?.onClick();
            close();
          }}
          className="shrink-0 text-sm font-medium text-primary underline-offset-4 hover:underline"
        >
          {item.action.label}
        </button>
      ) : null}
      <button
        type="button"
        onClick={close}
        className="shrink-0 rounded-md p-1 text-muted-foreground hover:bg-accent"
      >
        <X aria-hidden="true" className="size-4" />
        <span className="sr-only">{t('common.dismiss')}</span>
      </button>
    </div>
  );
}
